import isEqual from 'lodash/isEqual';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue, watch } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import {
  DomainReference,
  Optional,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  EmbeddableLocation,
  FreightAllowance,
  Order,
  OrderBillTo,
  OrderLineItem,
  OrderOrShipmentTemplate,
  OrderStatus,
  ReferenceNumber,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import { useReferenceNumberTypes } from './useReferenceNumberTypes';

export interface UseNewOrderWithDefaultsResponse {
  isLoading: ComputedRef<boolean>;
  isDirty: ComputedRef<boolean>;
  order: Ref<Order>;
  orderWithDefaults: Ref<Order>;
}

export const useNewOrderWithDefaults = (
  selectedTemplate: MaybeRefOrGetter<OrderOrShipmentTemplate | undefined>,
): UseNewOrderWithDefaultsResponse => {
  const { owningClient } = useParentEntityStore();
  const {
    getDefaultReferenceNumbers,
    lineItemReferenceNumberTypes,
    loadReferenceNumberTypes,
    orderReferenceNumberTypes,
  } = useReferenceNumberTypes();

  const order: Ref<Order> = ref(
    new Order({
      status: OrderStatus.PENDING,
      referenceNumbers: [new ReferenceNumber()],
      lineItems: [new OrderLineItem()],
      requestedAccessorials: [],
    }),
  );
  const orderWithDefaults: Ref<Order> = ref(new Order(order.value));
  const _isLoading = ref(false);

  const isLoading = computed(() => _isLoading.value);

  const isDirty = computed(() => {
    return !isEqual(order.value, orderWithDefaults.value);
  });

  const applyDefaultReferenceNumbers = async () => {
    await loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.ORDER,
      bypassCache: true,
    });
    const defaultReferenceNumbers = getDefaultReferenceNumbers(
      orderReferenceNumberTypes.value.data,
    );

    if (defaultReferenceNumbers.length) {
      order.value.referenceNumbers = defaultReferenceNumbers;
    }
  };

  const applyDefaultLineItemReferenceNumbers = async () => {
    await loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.LINE_ITEM,
      bypassCache: true,
    });

    const defaultLineItemReferenceNumbers = getDefaultReferenceNumbers(
      lineItemReferenceNumberTypes.value.data,
    );

    if (defaultLineItemReferenceNumbers.length) {
      const lineItem = order.value.lineItems[0];
      lineItem.referenceNumbers = defaultLineItemReferenceNumbers;
    }
  };

  const applyDefaultOrderData = async (
    template: Optional<OrderOrShipmentTemplate>,
  ) => {
    if (!authorization.hasAuthority('client', 'read')) {
      return;
    }

    const defaultOrderData = await api.client.getDefaultOrderData();

    if (!order.value.billTo || order.value.billTo.isEmpty) {
      order.value.billTo = defaultOrderData.billToAddress
        ? new OrderBillTo({
            location: EmbeddableLocation.fromLocation(
              defaultOrderData.billToAddress,
            ),
            billToDetails: defaultOrderData.billToAddress.billToDetails,
          })
        : new OrderBillTo();
    }

    const freightAllowanceEnabledIsDefinedOnTemplate =
      template?.templateData?.freightAllowance?.enabled != null;
    const orderAlreadyHasFreightAllowanceAmount =
      !!order.value.freightAllowance?.amount?.amount;
    if (
      !freightAllowanceEnabledIsDefinedOnTemplate &&
      !orderAlreadyHasFreightAllowanceAmount
    ) {
      order.value.freightAllowance = new FreightAllowance({
        enabled: defaultOrderData.freightAllowanceEnabled,
      });
    }
  };

  const applyTemplateAndDefaults = async (
    owner?: DomainReference,
    orderTemplate?: OrderOrShipmentTemplate,
  ) => {
    if (!owner?.id) {
      order.value = new Order();
      orderWithDefaults.value = new Order(order.value);
      return;
    }

    if (orderTemplate?.templateData) {
      order.value = new Order({
        ...(orderTemplate?.templateData as Order),
        owner,
      });
      await applyDefaultOrderData(orderTemplate);
    } else {
      _isLoading.value = true;
      order.value = new Order({ owner });
      await Promise.allSettled([
        applyDefaultReferenceNumbers(),
        applyDefaultLineItemReferenceNumbers(),
        applyDefaultOrderData(orderTemplate),
      ]);
      _isLoading.value = false;
    }

    orderWithDefaults.value = new Order(order.value);
  };

  watch(
    () => toValue(selectedTemplate),
    (newVal) => {
      if (!isEqual(order.value, newVal?.templateData)) {
        applyTemplateAndDefaults(owningClient.value, newVal);
      }
    },
    { immediate: true },
  );

  watch(
    owningClient,
    (newVal: Optional<DomainReference>, oldVal: Optional<DomainReference>) => {
      if (newVal?.id === oldVal?.id) {
        return;
      }

      applyTemplateAndDefaults(newVal, toValue(selectedTemplate));
    },
    { immediate: true },
  );

  return {
    isLoading,
    isDirty,
    order,
    orderWithDefaults,
  };
};
